/**
 * Colour readability checks for cosmetics: custom colour sets, name colours and the in-game rule
 * that a player's colours must stay readable and distinct from the other players' colours.
 * Pure functions; the client and the server use the same rules.
 */
import type { ColorSetParams, Hex, NameColorParams } from "./schema";

export interface Rgb8 {
  r: number;
  g: number;
  b: number;
}

const HEX_RE = /^#[0-9a-f]{6}$/;

export function isHex(v: unknown): v is Hex {
  return typeof v === "string" && HEX_RE.test(v);
}

export function hexToRgb(hex: Hex): Rgb8 {
  const n = parseInt(hex.slice(1), 16);
  return { r: (n >> 16) & 255, g: (n >> 8) & 255, b: n & 255 };
}

export function rgbToHex(c: Rgb8): Hex {
  const h = (v: number) => Math.max(0, Math.min(255, Math.round(v))).toString(16).padStart(2, "0");
  return `#${h(c.r)}${h(c.g)}${h(c.b)}`;
}

function channel(v: number): number {
  const s = v / 255;
  return s <= 0.03928 ? s / 12.92 : Math.pow((s + 0.055) / 1.055, 2.4);
}

/** WCAG relative luminance 0..1. */
export function luminance(c: Rgb8): number {
  return 0.2126 * channel(c.r) + 0.7152 * channel(c.g) + 0.0722 * channel(c.b);
}

/** WCAG contrast ratio 1..21. */
export function contrastRatio(a: Rgb8, b: Rgb8): number {
  const la = luminance(a);
  const lb = luminance(b);
  return (Math.max(la, lb) + 0.05) / (Math.min(la, lb) + 0.05);
}

/** Perceptual-ish colour distance ("redmean"), 0..~765. */
export function colorDistance(a: Rgb8, b: Rgb8): number {
  const rm = (a.r + b.r) / 2;
  const dr = a.r - b.r;
  const dg = a.g - b.g;
  const db = a.b - b.b;
  return Math.sqrt((2 + rm / 256) * dr * dr + 4 * dg * dg + (2 + (255 - rm) / 256) * db * db);
}

/** Minimum contrast between a colour set's border and territory. */
export const MIN_BORDER_CONTRAST = 2.2;
/** Territory luminance window (not near-black, not near-white: names and borders must read on it). */
export const MIN_TERRITORY_LUMINANCE = 0.025;
export const MAX_TERRITORY_LUMINANCE = 0.72;
/** Minimum contrast between a name colour and its outline. */
export const MIN_NAME_CONTRAST = 4.5;
/** Two players' territory colours closer than this are too alike (in-game fallback rule). */
export const MIN_PLAYER_DISTANCE = 45;
/** Typical terrain colours a territory is drawn over (grass, forest, sand, rock, snow, water). */
export const TERRAIN_SAMPLES: readonly Hex[] = ["#6a9c4a", "#3f6e35", "#d8c48a", "#8c8577", "#eef2f4", "#2a5f8f"];
/** A territory colour must differ at least this much from the terrain it covers on average. */
export const MIN_TERRAIN_DISTANCE = 70;

export type ColorProblem = "badColor" | "borderContrast" | "tooDark" | "tooBright" | "likeTerrain";

export interface ColorCheck {
  ok: boolean;
  problems: ColorProblem[];
  /** Border / territory contrast ratio (for the editor's meter). */
  contrast: number;
}

/** Contrast check of a colour set (custom colour set editor and catalog validation). */
export function checkColorSet(set: Pick<ColorSetParams, "territory" | "border">): ColorCheck {
  if (!isHex(set.territory) || !isHex(set.border)) return { ok: false, problems: ["badColor"], contrast: 1 };
  const terr = hexToRgb(set.territory);
  const border = hexToRgb(set.border);
  const problems: ColorProblem[] = [];
  const contrast = contrastRatio(terr, border);
  if (contrast < MIN_BORDER_CONTRAST) problems.push("borderContrast");
  const lum = luminance(terr);
  if (lum < MIN_TERRITORY_LUMINANCE) problems.push("tooDark");
  if (lum > MAX_TERRITORY_LUMINANCE) problems.push("tooBright");
  let nearest = Infinity;
  for (const s of TERRAIN_SAMPLES) nearest = Math.min(nearest, colorDistance(terr, hexToRgb(s)));
  if (nearest < MIN_TERRAIN_DISTANCE) problems.push("likeTerrain");
  return { ok: problems.length === 0, problems, contrast };
}

/** Contrast check of a name colour against its outline. */
export function checkNameColor(n: NameColorParams): ColorCheck {
  if (!isHex(n.color) || !isHex(n.outline)) return { ok: false, problems: ["badColor"], contrast: 1 };
  const contrast = contrastRatio(hexToRgb(n.color), hexToRgb(n.outline));
  return { ok: contrast >= MIN_NAME_CONTRAST, problems: contrast >= MIN_NAME_CONTRAST ? [] : ["borderContrast"], contrast };
}

/**
 * In-game rule (every client computes the same result from the same loadouts): a player's custom
 * territory colour is used only when it stays far enough from every other player's colour. Players
 * are processed in id order; each accepted custom colour then counts for later players.
 *
 * @param base   assigned colour per player id (from the game's palette)
 * @param wanted custom territory colour per player id (only players that wear a colour set)
 * @returns the player ids whose custom colours are accepted
 */
export function acceptCustomColors(base: ReadonlyMap<number, Rgb8>, wanted: ReadonlyMap<number, Rgb8>): Set<number> {
  const accepted = new Set<number>();
  const ids = [...wanted.keys()].sort((a, b) => a - b);
  for (const id of ids) {
    const c = wanted.get(id)!;
    let ok = true;
    for (const [other, oc] of base) {
      if (other === id) continue;
      const col = accepted.has(other) ? wanted.get(other)! : oc;
      if (colorDistance(c, col) < MIN_PLAYER_DISTANCE) {
        ok = false;
        break;
      }
    }
    if (ok) accepted.add(id);
  }
  return accepted;
}
